import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { format, parseISO, isToday, isYesterday, isThisWeek, isThisMonth } from 'date-fns';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { translateZoneName, translateZoneNameToEnglish, clearTranslationCache } from '@medical/shared/utils/translateZoneName';
import { TranslatedText, TranslatedTextArray } from '@medical/shared/components/TranslatedText';
import i18n from '@medical/shared/i18n';

const HistoryContainer = styled.div`
  max-width: ${props => props.compact ? '100%' : '1200px'};
  margin: ${props => props.compact ? '0' : '0 auto'};
  padding: ${props => props.compact ? '0' : '2rem 1rem'};

  @media (max-width: 768px) {
    padding: ${props => props.compact ? '0' : '1rem 0.75rem'};
  }

  @media (max-width: 480px) {
    padding: ${props => props.compact ? '0' : '0.75rem 0.5rem'};
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin: 0 0 0.25rem 0;
  }
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1.125rem;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
`;

const ControlsSection = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 1rem;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    margin-bottom: 1rem;
    gap: 0.5rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    width: 100%;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const FilterLabel = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: white;
  font-size: 0.875rem;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    font-size: 0.8125rem;
  }
`;

const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    font-size: 0.8125rem;
  }
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  text-align: center;

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  letter-spacing: 0.05em;

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const HistoryList = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const HistoryHeader = styled.div`
  background: #f8fafc;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
  }
`;

const HistoryTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
`;

const RefreshButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #2563eb;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.625rem 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8125rem;
    padding: 0.5rem 0.75rem;
  }
`;

const ClearButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 0.5rem;
  white-space: nowrap;

  &:hover {
    background: #dc2626;
  }

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    margin-top: 0.5rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8125rem;
    padding: 0.5rem 0.75rem;
  }
`;

const DeleteModeButton = styled.button`
  background: ${props => props.active ? '#dc2626' : '#ef4444'};
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 0.5rem;
  white-space: nowrap;

  &:hover {
    background: #dc2626;
  }

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    margin-top: 0.5rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8125rem;
    padding: 0.5rem 0.75rem;
  }
`;

const DeleteModeControls = styled.div`
  background: #fef2f2;
  border: 2px solid #fecaca;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const DeleteModeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #991b1b;
  font-weight: 500;
`;

const DeleteModeActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  white-space: nowrap;

  &.primary {
    background: #3b82f6;
    color: white;
    &:hover {
      background: #2563eb;
    }
  }

  &.danger {
    background: #ef4444;
    color: white;
    &:hover {
      background: #dc2626;
    }
  }

  &.secondary {
    background: #6b7280;
    color: white;
    &:hover {
      background: #4b5563;
    }
  }

  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
    font-size: 0.8125rem;
    padding: 0.5rem 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    padding: 0.5rem;
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin-right: 0.75rem;
  flex-shrink: 0;
`;

const ClearDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const ClearDropdownContent = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 10;
  min-width: 200px;
  margin-top: 0.5rem;
`;

const ClearDropdownItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  border: none;
  background: white;
  color: #374151;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f3f4f6;
  }

  &:first-child {
    border-radius: 0.5rem 0.5rem 0 0;
  }

  &:last-child {
    border-radius: 0 0 0.5rem 0.5rem;
  }

  &.danger {
    color: #ef4444;
  }
`;

const HistoryItem = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    gap: 0.5rem;
  }
`;

const PainIndicator = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${props => {
    // If painColor is provided and valid, use it (prioritize stored color)
    if (props.painColor && props.painColor.startsWith('#')) {
      return props.painColor;
    }
    // Otherwise, map from painLevel
    switch (props.painLevel) {
      case 'no-pain': return '#10b981';
      case 'mild': return '#60a5fa';
      case 'moderate': return '#f59e0b';
      case 'severe': return '#f97316';
      case 'worst-pain': return '#ef4444';
      default: return '#e5e7eb';
    }
  }};
  flex-shrink: 0;
  border: 2px solid white;
  box-shadow: 0 0 0 2px ${props => {
    // If painColor is provided and valid, use it (prioritize stored color)
    if (props.painColor && props.painColor.startsWith('#')) {
      return props.painColor;
    }
    // Otherwise, map from painLevel
    switch (props.painLevel) {
      case 'no-pain': return '#10b981';
      case 'mild': return '#60a5fa';
      case 'moderate': return '#f59e0b';
      case 'severe': return '#f97316';
      case 'worst-pain': return '#ef4444';
      default: return '#e5e7eb';
    }
  }};
`;

const HistoryContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const HistoryMain = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const ZoneName = styled.div`
  font-weight: 600;
  color: #111827;
  font-size: 1rem;
  word-break: break-word;
  min-width: 0;
  flex: 1 1 8rem;
  overflow: visible;

  @media (max-width: 480px) {
    font-size: 0.9375rem;
    flex: 1 1 100%;
  }
`;

const PainLevel = styled.div`
  background: ${props => {
    // If painColor is provided and valid, use it (prioritize stored color)
    if (props.painColor && props.painColor.startsWith('#')) {
      return props.painColor;
    }
    // Otherwise, map from painLevel
    switch (props.painLevel) {
      case 'no-pain': return '#10b981';
      case 'mild': return '#60a5fa';
      case 'moderate': return '#f59e0b';
      case 'severe': return '#f97316';
      case 'worst-pain': return '#ef4444';
      default: return '#6b7280';
    }
  }};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
`;

const ViewBadge = styled.div`
  background: ${props => props.view === 'front' ? '#3b82f6' : '#8b5cf6'};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
`;

const HistoryDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.875rem;
  color: #6b7280;

  @media (max-width: 768px) {
    gap: 0.75rem;
    font-size: 0.8125rem;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
    font-size: 0.75rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const HistoryMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    align-items: flex-start;
    width: 100%;
  }

  @media (max-width: 480px) {
    gap: 0.125rem;
  }
`;

const Timestamp = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  word-break: break-word;

  @media (max-width: 480px) {
    font-size: 0.8125rem;
  }
`;

const RelativeTime = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;

  @media (max-width: 480px) {
    font-size: 0.6875rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
`;

const EmptyText = styled.p`
  margin: 0;
`;

const History = ({ patientId = null, compact = false }) => {
  const { t, i18n } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render on language change
  
  // Listen to language changes and refresh
  useEffect(() => {
    const handleLanguageChange = () => {
      clearTranslationCache(); // Clear cache when language changes
      setRefreshKey(prev => prev + 1); // Force component re-render
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);
  
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Helper function to get translations - respects current language preference
  const getT = (key, options = {}) => {
    return t(key, options);
  };
  
  // Ensure historyData is always an array
  const safeHistoryData = Array.isArray(historyData) ? historyData : [];
  const [filters, setFilters] = useState({
    painLevel: 'all',
    view: 'all',
    dateRange: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0
  });
  const [showClearDropdown, setShowClearDropdown] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState(new Set());

  useEffect(() => {
    fetchHistoryData();
    // Set up auto-refresh every 30 seconds only for patient view
    if (!patientId) {
      const interval = setInterval(fetchHistoryData, 30000);
      return () => clearInterval(interval);
    }
  }, [patientId]);

  // Debounce search: update debouncedSearchTerm 400ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Refetch data when filters or debounced search change (not on every keystroke)
  useEffect(() => {
    fetchHistoryData();
  }, [filters, debouncedSearchTerm, patientId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showClearDropdown && !event.target.closest('.clear-dropdown')) {
        setShowClearDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showClearDropdown]);

  const fetchHistoryData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (patientId) {
        // Fetch for specific patient (doctor view)
        const token = localStorage.getItem('doctorToken');
        response = await axios.get(`/api/pain-logs/patient/${patientId}`, {
          params: {
            limit: 500,
            sort: 'desc'
          },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const painLogs = response.data.data?.painLogs || [];
        // Apply filters client-side
        let filtered = painLogs;
        if (filters.painLevel !== 'all') {
          filtered = filtered.filter(log => log.painLevel === filters.painLevel);
        }
        if (filters.view !== 'all') {
          filtered = filtered.filter(log => log.view === filters.view);
        }
        if (debouncedSearchTerm) {
          const searchLower = debouncedSearchTerm.toLowerCase();
          filtered = filtered.filter(log => 
            log.zoneName?.toLowerCase().includes(searchLower) ||
            log.notes?.toLowerCase().includes(searchLower)
          );
        }
        setHistoryData(filtered);
        calculateStats(filtered);
      } else {
        // Normal patient view
        response = await axios.get('/api/pain-logs/history', {
          params: {
            limit: 500,
            painLevel: filters.painLevel !== 'all' ? filters.painLevel : undefined,
            view: filters.view !== 'all' ? filters.view : undefined,
            search: debouncedSearchTerm || undefined
          }
        });
        const painLogs = response.data?.painLogs;
        const safePainLogs = Array.isArray(painLogs) ? painLogs : [];
        setHistoryData(safePainLogs);
        calculateStats(safePainLogs);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching history data:', err);
      const errorMessage = err.response?.data?.message || err.message || getT('history.error.message');
      setError(errorMessage);
      setHistoryData([]);
      setLoading(false);
      toast.error(`${getT('history.error.title')}: ${errorMessage}`);
    }
  };

  const calculateStats = (data) => {
    // BULLETPROOF VERSION 2.0 - Fixed all filter errors
    const safeData = Array.isArray(data) ? data : [];
    const now = new Date();
    
    // Filter with safe timestamp handling
    const today = safeData.filter(item => {
      try {
        return item?.timestamp && isToday(parseISO(item.timestamp));
      } catch (e) {
        return false;
      }
    });
    
    const thisWeek = safeData.filter(item => {
      try {
        return item?.timestamp && isThisWeek(parseISO(item.timestamp));
      } catch (e) {
        return false;
      }
    });
    
    const thisMonth = safeData.filter(item => {
      try {
        return item?.timestamp && isThisMonth(parseISO(item.timestamp));
      } catch (e) {
        return false;
      }
    });

    setStats({
      total: safeData.length,
      today: today.length,
      thisWeek: thisWeek.length,
      thisMonth: thisMonth.length
    });
  };

  const getPainLevelText = (level) => {
    return getT(`history.painLevels.${level}`, { defaultValue: level });
  };

  // Helper function to get translated view text
  const getViewText = (view) => {
    if (!view) return '';
    return getT(`bodyDiagram.view.${view}`, { defaultValue: view });
  };

  // Helper function to format dates with localization
  const formatLocalizedDate = (date, options = {}) => {
    try {
      const lang = i18n.language || 'en';
      // Map i18n language codes to Intl locale codes
      const localeMap = {
        'en': 'en-US',
        'kn': 'kn-IN',
        'ta': 'ta-IN',
        'ml': 'ml-IN',
        'hi': 'hi-IN',
        'mr': 'mr-IN',
        'te': 'te-IN'
      };
      const locale = localeMap[lang] || 'en-US';
      
      const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      
      return new Intl.DateTimeFormat(locale, { ...defaultOptions, ...options }).format(date);
    } catch (e) {
      // Fallback to date-fns if Intl fails
      return format(date, 'MMM d, yyyy h:mm a');
    }
  };

  const getRelativeTime = (timestamp) => {
    try {
      if (!timestamp) return getT('history.labels.unknownTime');
      const date = parseISO(timestamp);
      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      
      const lang = i18n.language || 'en';
      const localeMap = {
        'en': 'en-US',
        'kn': 'kn-IN',
        'ta': 'ta-IN',
        'ml': 'ml-IN',
        'hi': 'hi-IN',
        'mr': 'mr-IN',
        'te': 'te-IN'
      };
      const locale = localeMap[lang] || 'en-US';
      
      // Show precise time for recent entries
      const timeString = new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(date);
      
      if (diffInMinutes < 1) return `${timeString} (${getT('history.labels.justNow')})`;
      if (diffInMinutes < 60) return `${timeString} (${diffInMinutes}${getT('history.labels.minutesAgo')})`;
      if (diffInMinutes < 1440) return `${timeString} (${Math.floor(diffInMinutes / 60)}${getT('history.labels.hoursAgo')})`;
      if (diffInMinutes < 10080) {
        const dateStr = new Intl.DateTimeFormat(locale, {
          month: 'short',
          day: 'numeric'
        }).format(date);
        return `${dateStr} ${timeString}`;
      }
      const dateStr = new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).format(date);
      return `${dateStr} ${timeString}`;
    } catch (e) {
      return t('history.labels.invalidDate');
    }
  };

  const filteredData = safeHistoryData.filter(item => {
    // Ensure item exists and has required properties
    if (!item || typeof item !== 'object') {
      return false;
    }
    
    // In delete mode, only show previous logs (not today's)
    if (deleteMode && item.timestamp) {
      try {
        const itemDate = parseISO(item.timestamp);
        if (isToday(itemDate)) {
          return false; // Hide today's logs in delete mode
        }
      } catch (e) {
        // If timestamp parsing fails, include it (safer approach)
      }
    }
    
    // Pain level filter
    if (filters.painLevel !== 'all' && item.painLevel !== filters.painLevel) {
      return false;
    }
    
    // View filter
    if (filters.view !== 'all' && item.view !== filters.view) {
      return false;
    }
    
    // Date range filter
    if (filters.dateRange !== 'all' && item.timestamp) {
      try {
        const itemDate = parseISO(item.timestamp);
        const now = new Date();
        
        switch (filters.dateRange) {
          case 'today':
            if (!isToday(itemDate)) return false;
            break;
          case 'week':
            if (!isThisWeek(itemDate)) return false;
            break;
          case 'month':
            if (!isThisMonth(itemDate)) return false;
            break;
        }
      } catch (e) {
        // If timestamp parsing fails, exclude this item from date filtering
        return filters.dateRange === 'all';
      }
    }
    
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        (item.zoneName && item.zoneName.toLowerCase().includes(searchLower)) ||
        (item.painLevel && item.painLevel.toLowerCase().includes(searchLower)) ||
        (item.notes && item.notes.toLowerCase().includes(searchLower)) ||
        (Array.isArray(item.symptoms) && item.symptoms.some(s => s && s.toLowerCase().includes(searchLower))) ||
        (Array.isArray(item.triggers) && item.triggers.some(t => t && t.toLowerCase().includes(searchLower)))
      );
    }
    
    return true;
  });

  // Stats from the currently displayed list so cards and list are never out of sync
  const displayStats = (() => {
    const safe = filteredData;
    const todayCount = safe.filter(item => {
      try { return item?.timestamp && isToday(parseISO(item.timestamp)); } catch (e) { return false; }
    }).length;
    const weekCount = safe.filter(item => {
      try { return item?.timestamp && isThisWeek(parseISO(item.timestamp)); } catch (e) { return false; }
    }).length;
    const monthCount = safe.filter(item => {
      try { return item?.timestamp && isThisMonth(parseISO(item.timestamp)); } catch (e) { return false; }
    }).length;
    return { total: safe.length, today: todayCount, thisWeek: weekCount, thisMonth: monthCount };
  })();

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleRefresh = () => {
    fetchHistoryData();
  };

  const handleDeleteMode = () => {
    setDeleteMode(true);
    setSelectedLogs(new Set());
    setShowClearDropdown(false);
  };

  const handleCancelDeleteMode = () => {
    setDeleteMode(false);
    setSelectedLogs(new Set());
  };

  const handleToggleLogSelection = (logId) => {
    setSelectedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedLogs.size === filteredData.length) {
      // Deselect all
      setSelectedLogs(new Set());
    } else {
      // Select all
      setSelectedLogs(new Set(filteredData.map(log => log._id || log.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedLogs.size === 0) {
      toast.error(getT('history.deleteMode.selectAtLeastOne'));
      return;
    }

    if (!window.confirm(getT('history.deleteMode.confirm', { count: selectedLogs.size }))) {
      return;
    }

    try {
      const deletePromises = Array.from(selectedLogs).map(logId => 
        axios.delete(`/api/pain-logs/${logId}`)
      );
      
      await Promise.all(deletePromises);
      setDeleteMode(false);
      setSelectedLogs(new Set());
      fetchHistoryData();
      toast.success(getT('history.deleteMode.success', { count: selectedLogs.size }));
    } catch (err) {
      console.error('Error deleting logs:', err);
      toast.error(getT('history.deleteMode.error'));
    }
  };

  // Only show full-page spinner on initial load (no data yet). When refetching
  // after search/filter change, keep the UI visible so the search bar stays usable.
  if (loading && safeHistoryData.length === 0) {
    return <LoadingSpinner text={getT('history.loading')} />;
  }

  if (error) {
    return (
      <HistoryContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>{getT('history.error.title')}</h2>
          <p>{error}</p>
          <button onClick={fetchHistoryData} className="btn btn-primary">
            {getT('history.error.retry')}
          </button>
        </div>
      </HistoryContainer>
    );
  }

  return (
    <HistoryContainer compact={compact}>
      {!compact && (
        <Header>
          <Title>{getT('history.title')}</Title>
          <Subtitle>{getT('history.subtitle')}</Subtitle>
        </Header>
      )}

      <StatsSection>
        <StatCard>
          <StatValue>{displayStats.total}</StatValue>
          <StatLabel>{getT('history.stats.total')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{displayStats.today}</StatValue>
          <StatLabel>{getT('history.stats.today')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{displayStats.thisWeek}</StatValue>
          <StatLabel>{getT('history.stats.thisWeek')}</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{displayStats.thisMonth}</StatValue>
          <StatLabel>{getT('history.stats.thisMonth')}</StatLabel>
        </StatCard>
      </StatsSection>

      <ControlsSection>
        <FilterGroup>
          <FilterLabel>{getT('history.filters.painLevel')}</FilterLabel>
          <Select
            value={filters.painLevel}
            onChange={(e) => handleFilterChange('painLevel', e.target.value)}
          >
            <option value="all">{getT('history.filters.allLevels')}</option>
            <option value="no-pain">{getT('history.filters.noPain')}</option>
            <option value="mild">{getT('history.filters.mild')}</option>
            <option value="moderate">{getT('history.filters.moderatePain')}</option>
            <option value="severe">{getT('history.filters.severePain')}</option>
            <option value="worst-pain">{getT('history.filters.worstPain')}</option>
          </Select>

          <FilterLabel>{getT('history.filters.view')}</FilterLabel>
          <Select
            value={filters.view}
            onChange={(e) => handleFilterChange('view', e.target.value)}
          >
            <option value="all">{getT('history.filters.bothViews')}</option>
            <option value="front">{getT('history.filters.frontView')}</option>
            <option value="back">{getT('history.filters.backView')}</option>
          </Select>

          <FilterLabel>{getT('history.filters.dateRange')}</FilterLabel>
          <Select
            value={filters.dateRange}
            onChange={(e) => handleFilterChange('dateRange', e.target.value)}
          >
            <option value="all">{getT('history.filters.allTime')}</option>
            <option value="today">{getT('history.filters.today')}</option>
            <option value="week">{getT('history.filters.thisWeek')}</option>
            <option value="month">{getT('history.filters.thisMonth')}</option>
          </Select>
        </FilterGroup>

        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          alignItems: 'center',
          flexWrap: 'wrap',
          width: '100%'
        }}>
          <SearchInput
            type="text"
            placeholder={getT('history.search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: '1 1 200px' }}
          />
          <RefreshButton onClick={handleRefresh}>
            🔄 {getT('history.actions.refresh')}
          </RefreshButton>
          {!patientId && (
            <DeleteModeButton 
              active={deleteMode}
              onClick={handleDeleteMode}
            >
              {getT('history.actions.deleteLogs')}
            </DeleteModeButton>
          )}
        </div>
      </ControlsSection>

      {deleteMode && (
        <DeleteModeControls>
          <DeleteModeInfo>
            <span>{getT('history.deleteMode.title')}</span>
            <span style={{ fontSize: '0.875rem', color: '#991b1b' }}>
              ({selectedLogs.size} {getT('history.deleteMode.selected')})
            </span>
          </DeleteModeInfo>
          <DeleteModeActions>
            <ActionButton className="primary" onClick={handleSelectAll}>
              {selectedLogs.size === filteredData.length ? getT('history.actions.deselectAll') : getT('history.actions.selectAll')}
            </ActionButton>
            <ActionButton 
              className="danger" 
              onClick={handleDeleteSelected}
              disabled={selectedLogs.size === 0}
              style={{ opacity: selectedLogs.size === 0 ? 0.5 : 1 }}
            >
              {getT('history.actions.deleteSelected')} ({selectedLogs.size})
            </ActionButton>
            <ActionButton className="secondary" onClick={handleCancelDeleteMode}>
              {getT('history.actions.cancel')}
            </ActionButton>
          </DeleteModeActions>
        </DeleteModeControls>
      )}

      <HistoryList>
        <HistoryHeader>
          <HistoryTitle>
            {getT('history.header.title', { count: filteredData.length })}
          </HistoryTitle>
        </HistoryHeader>

        {filteredData.length === 0 ? (
          <EmptyState>
            <EmptyIcon>📊</EmptyIcon>
            <EmptyTitle>{getT('history.empty.title')}</EmptyTitle>
            <EmptyText>
              {searchTerm || filters.painLevel !== 'all' || filters.view !== 'all' || filters.dateRange !== 'all'
                ? getT('history.empty.filtered')
                : getT('history.empty.noData')
              }
            </EmptyText>
          </EmptyState>
        ) : (
          filteredData.map((record) => {
            const logId = record._id || record.id;
            const isSelected = selectedLogs.has(logId);

            return (
              <HistoryItem 
                key={`${logId}-${i18n.language}-${refreshKey}`}
                style={{
                  background: isSelected && deleteMode ? '#fef2f2' : undefined,
                  borderColor: isSelected && deleteMode ? '#ef4444' : undefined
                }}
              >
                {deleteMode && (
                  <Checkbox
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleLogSelection(logId)}
                  />
                )}
                <PainIndicator painLevel={record.painLevel} painColor={record.painColor} />
              
              <HistoryContent>
                <HistoryMain>
                  <ZoneName>{patientId ? translateZoneNameToEnglish(record.zoneName) : translateZoneName(record.zoneName, t)}</ZoneName>
                  <PainLevel painLevel={record.painLevel} painColor={record.painColor}>
                    {getPainLevelText(record.painLevel)}
                  </PainLevel>
                  <ViewBadge view={record.view}>
                    {getViewText(record.view)}
                  </ViewBadge>
                </HistoryMain>
                
                <HistoryDetails>
                  {record.painIntensity > 0 && (
                    <DetailItem>
                      <span>📊</span>
                      <span>{getT('history.labels.intensity')} {record.painIntensity}/10</span>
                    </DetailItem>
                  )}
                  {record.notes && (
                    <DetailItem>
                      <span>📝</span>
                      <span><TranslatedText text={record.notes} /></span>
                    </DetailItem>
                  )}
                  {record.symptoms && record.symptoms.length > 0 && (
                    <DetailItem>
                      <span>🩺</span>
                      <span>{getT('history.labels.symptoms')} <TranslatedTextArray texts={record.symptoms} separator=", " /></span>
                    </DetailItem>
                  )}
                  {record.triggers && record.triggers.length > 0 && (
                    <DetailItem>
                      <span>⚡</span>
                      <span>{getT('history.labels.triggers')} <TranslatedTextArray texts={record.triggers} separator=", " /></span>
                    </DetailItem>
                  )}
                </HistoryDetails>
              </HistoryContent>
              
              <HistoryMeta>
                <Timestamp>
                  {record.timestamp ? (() => {
                    try {
                      return formatLocalizedDate(parseISO(record.timestamp));
                    } catch (e) {
                      return getT('history.labels.invalidDate');
                    }
                  })() : getT('history.labels.noDate')}
                </Timestamp>
                <RelativeTime>
                  {getRelativeTime(record.timestamp)}
                </RelativeTime>
              </HistoryMeta>
            </HistoryItem>
            );
          })
        )}
      </HistoryList>
    </HistoryContainer>
  );
};

export default History;
